import type { ECommunityRole, ED6SWCommunityRole } from '~/entities/container/community/interfaces/community'
import { toCommunityRole, toCommunityRoleFromD6SW } from '~/entities/container/community/interfaces/community'
import { EUserContentType, UserState } from '~/entities/user/user/interfaces/user'

import { extractContents } from '~/features-shared/content/api/parser-exalead'
import type { IFedSearchAttribute, IFedSearchInfo, IFedSearchResponse, IMember, IMemberSwym } from '~/features-shared/member/interfaces/search'

import type { ExaleadResponse, SwymResponse } from '~/interfaces/services-dto'

export function userInfoParser(responses: IFedSearchResponse, getAllResponses?: boolean) {
  const usersInfo: IFedSearchInfo[] = []
  if (responses.results) {
    if (getAllResponses) {
      responses.results.forEach((result) => {
        usersInfo.push(_searchAllResponsesParser(result.attributes))
      })
    }
    else {
      responses.results.forEach((result) => {
        usersInfo.push(_searchResponseParser(result.attributes))
      })
    }
    return usersInfo
  }
  return []
}

export function userInfoParserWithLogin(responses: IFedSearchResponse) {
  const usersInfo: IFedSearchInfo[] = []
  if (responses.results) {
    responses.results.forEach((result) => {
      usersInfo.push(_searchAllResponsesWithLoginParser(result.attributes))
    })
    return usersInfo
  }
  return []
}

export function userMembersListParser(response: SwymResponse<IMemberSwym[]>): IMember[] {
  return response.result.map(member => _membersListResponseParser(member))
}

function _searchResponseParser(attributes: IFedSearchAttribute[]) {
  const parsedUserInfo: IFedSearchInfo = {
    resource_id: '',
    type_icon_url: '',
    full_name: '',
    login: '',
    contentType: '',
    source_id: '',
    format: '',
    id: '',

  }
  attributes.forEach((attribute) => {
    switch (attribute.name) {
      case 'resourceid' :
        parsedUserInfo.resource_id = attribute.value
        parsedUserInfo.login = getProperValue(attribute.value)
        break
      case 'type_icon_url' :
        parsedUserInfo.type_icon_url = attribute.value
        break
      case 'ds6w:label' :
        parsedUserInfo.full_name = attribute.value
        parsedUserInfo.id = `@${attribute.value}`
        break
      case 'ds6w:type' :
        parsedUserInfo.contentType = getProperValue(attribute.value)
        break
      case 'sourceid' :
        parsedUserInfo.source_id = attribute.value
        parsedUserInfo.format = attribute.format
        break
      case 'ds6w:resourceUid':
        parsedUserInfo.uid = attribute.value
        break
    }
  })
  return parsedUserInfo
}
function _searchAllResponsesParser(attributes: IFedSearchAttribute[]) {
  const parsedUserInfo: IFedSearchInfo = {
    resource_id: '',
    type_icon_url: '',
    full_name: '',
    login: '',
    contentType: '',
    source_id: '',
    format: '',
    id: '',
  }
  attributes.forEach((attribute) => {
    switch (attribute.name) {
      case 'resourceid' :
        parsedUserInfo.resource_id = attribute.value
        parsedUserInfo.login = getProperValue(attribute.value)
        break
      case 'type_icon_url' :
        parsedUserInfo.type_icon_url = attribute.value
        break
      case 'ds6w:label' :
        parsedUserInfo.full_name = attribute.value
        parsedUserInfo.id = parsedUserInfo.contentType === 'DirectMessage' ? (parsedUserInfo.resource_id || '').split(':')[4] : `@${attribute.value}`
        break
      case 'ds6w:type' :
        parsedUserInfo.contentType = getProperValue(attribute.value)
        break
      case 'sourceid' :
        parsedUserInfo.source_id = attribute.value
        parsedUserInfo.format = attribute.format
        break
      case 'ds6w:resourceUid':
        parsedUserInfo.uid = attribute.value
        break
    }
  })
  return parsedUserInfo
}

function _searchAllResponsesWithLoginParser(attributes: IFedSearchAttribute[]) {
  const parsedUserInfo: IFedSearchInfo = {
    resource_id: '',
    type_icon_url: '',
    full_name: '',
    login: '',
    contentType: '',
    source_id: '',
    format: '',
    id: '',

  }
  attributes.forEach((attribute) => {
    switch (attribute.name) {
      case 'resourceid' :
        parsedUserInfo.resource_id = attribute.value
        break
      case 'type_icon_url' :
        parsedUserInfo.type_icon_url = attribute.value
        break
      case 'ds6w:label' :
        parsedUserInfo.full_name = attribute.value
        parsedUserInfo.id = parsedUserInfo.contentType === 'DirectMessage' ? (parsedUserInfo.resource_id || '').split(':')[4] : `@${attribute.value}`
        break
      case 'ds6w:type' :
        parsedUserInfo.contentType = getProperValue(attribute.value)
        break
      case 'sourceid' :
        parsedUserInfo.source_id = attribute.value
        parsedUserInfo.format = attribute.format
        break
      case 'login' :
        parsedUserInfo.login = attribute.value
        break
      case 'ds6w:resourceUid':
        parsedUserInfo.uid = attribute.value
        break
    }
  })
  return parsedUserInfo
}

function getProperValue(value: string) {
  const newValue = value.split(':')
  return newValue[1]
}

function _membersListResponseParser(member: IMemberSwym): IMember {
  let contentType: EUserContentType = EUserContentType.Person
  if (member.member_type === 'users_group')
    contentType = EUserContentType.Group

  return {
    created_at: member.created_at,
    first_name: member.first_name,
    last_name: member.last_name,
    full_name: contentType === 'person'
      ? member.first_name && member.last_name ? `${member.first_name} ${member.last_name}` : member.login
      : member.first_name ? member.first_name : member.login,
    subject_uri: member.subject_uri,
    country_code: member.country_code,
    login: member.login,
    contentType,
    role: toCommunityRole(Number.parseInt(member.relation) as ECommunityRole),
    format: 'internal', // TODO: check if there are differences with external users
    uid: member.id,
    id: `@${member.first_name} ${member.last_name}`,
    idea: Number.parseInt(member.nb_ideation),
    media: Number.parseInt(member.nb_media),
    post: Number.parseInt(member.nb_post),
    question: Number.parseInt(member.nb_question),
    survey: Number.parseInt(member.nb_survey),
    comment: Number.parseInt(member.nb_comment),
    answer: Number.parseInt(member.nb_answer),
    derived: member.nb_derived_types?.reduce((sum, el) => sum + el.count, 0),
    country: member.country_code,
    members: member.member_type === 'tenant_user' ? 0 : ((member.details && Number.parseInt(member.details.nb_members) || 0)),
    external: !!(member.ut_invitation_email),
    state: member.ut_state || UserState.Active,
  }
}

export function userMembersSearchParser(responses: ExaleadResponse): IMember[] {
  const extracted = extractContents(responses)

  const parsed: IMember[] = extracted.map(member => ({
    answer: 0,
    comment: 0,
    contentType: EUserContentType.Person,
    country_code: member.ds6w_58_country?.split('.')[1],
    country: member.ds6w_58_country?.split('.')[1],
    created_at: member.crdate,
    derived: 0,
    external: !!(member.ut_invitation_email),
    first_name: member.first_name,
    format: 'internal', // TODO: check if there are differences with external users
    full_name: member.title || member.login,
    id: member.id,
    uid: member.ds6w_58_resourceUid,
    idea: 0,
    last_name: member.last_name,
    login: member.login,
    media: 0,
    post: 0,
    question: 0,
    survey: 0,
    members: member.member_type === 'tenant_user' ? 0 : member.details && member.details.nb_members,
    state: member.ut_state.toString() as UserState,
    role: toCommunityRoleFromD6SW(member.user_community_role as ED6SWCommunityRole),
    subject_uri: '',
    updated_at: member.update,
  } as IMember))

  return parsed
}
